// Hook to fetch family compatibility data (members + yogas)
// Uses cached data from database, provides function to generate missing yogas
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import { FamilyMember } from '../utils/familyPatternDetector';
import { batchGenerateFamilyYogas, checkFamilyYogaStatus } from '../utils/familyYogaGenerator';

interface FamilyYoga {
  member: string;
  yogas: Array<{ yoga_name: string; type: string; strength: string }>;
}

interface UseFamilyCompatibilityDataResult {
  members: FamilyMember[];
  familyYogas: FamilyYoga[];
  loading: boolean;
  error: string | null;
  generateMissingYogas: () => Promise<void>;
  generatingYogas: boolean;
  yogaGenerationProgress: { current: number; total: number; currentMember: string } | null;
}

export function useFamilyCompatibilityData(
  userId: string | null,
  selectedProfileId: string | null
): UseFamilyCompatibilityDataResult {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [familyYogas, setFamilyYogas] = useState<FamilyYoga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingYogas, setGeneratingYogas] = useState(false);
  const [yogaGenerationProgress, setYogaGenerationProgress] = useState<{ 
    current: number; 
    total: number; 
    currentMember: string 
  } | null>(null);

  const loadData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);

        // Determine primary profile ID
        let primaryProfileId = selectedProfileId;
        
        if (!primaryProfileId || primaryProfileId === 'my_profile') {
          // Find user's Self profile
          const { data: selfProfile } = await supabase
            .from('birth_profiles')
            .select('id')
            .eq('owner_id', userId)
            .or('relationship.eq.Self,relationship.is.null')
            .order('created_at', { ascending: true })
            .limit(1)
            .single();
          
          primaryProfileId = selfProfile?.id || null;
        }

        if (!primaryProfileId) {
          console.log('[FamilyCompatibility] No primary profile found');
          setMembers([]);
          setFamilyYogas([]);
          setLoading(false);
          return;
        }

        console.log('[FamilyCompatibility] Loading family data for primary profile:', primaryProfileId);

        // Load family members with chart_data
        const { data: familyProfiles, error: familyError } = await supabase
          .from('birth_profiles')
          .select('id, full_name, relationship, chart_data')
          .eq('family_of_profile_id', primaryProfileId)
          .order('full_name');

        if (familyError) throw familyError;

        console.log('[FamilyCompatibility] Found family profiles:', familyProfiles?.length || 0);

        // Load primary profile too
        const { data: primaryProfile } = await supabase
          .from('birth_profiles')
          .select('id, full_name, relationship, chart_data')
          .eq('id', primaryProfileId)
          .single();

        // Combine primary + family members
        const allMembers: FamilyMember[] = [
          ...(primaryProfile ? [{
            id: primaryProfile.id,
            full_name: primaryProfile.full_name,
            relationship: primaryProfile.relationship || 'Self',
            chart_data: primaryProfile.chart_data
          }] : []),
          ...(familyProfiles || []).map((fp: any) => ({
            id: fp.id,
            full_name: fp.full_name,
            relationship: fp.relationship || 'Other',
            chart_data: fp.chart_data
          }))
        ];

        console.log('[FamilyCompatibility] All members:', allMembers.length, allMembers);
        setMembers(allMembers);

        // Load yogas for all members (primary + family)
        const memberIds = allMembers.map(m => m.id);
        console.log('[FamilyCompatibility] Loading yogas for member IDs:', memberIds);
        
        if (memberIds.length > 0) {
          const { data: yogasData, error: yogasError } = await supabase
            .from('user_yogas')
            .select('birth_profile_id, yoga_name, type, strength, category')
            .in('birth_profile_id', memberIds)
            .eq('is_present', true); // Only active yogas

          if (yogasError) throw yogasError;
          console.log('[FamilyCompatibility] Found yogas:', yogasData?.length || 0, yogasData);

          // Group yogas by member
          const yogasByMember = new Map<string, Array<{ yoga_name: string; type: string; strength: string }>>();
          
          (yogasData || []).forEach((yoga: any) => {
            const member = allMembers.find(m => m.id === yoga.birth_profile_id);
            if (!member) return;
            
            if (!yogasByMember.has(member.full_name)) {
              yogasByMember.set(member.full_name, []);
            }
            yogasByMember.get(member.full_name)!.push({
              yoga_name: yoga.yoga_name,
              type: yoga.type,
              strength: yoga.strength
            });
          });

          const formattedYogas: FamilyYoga[] = Array.from(yogasByMember.entries()).map(([member, yogas]) => ({
            member,
            yogas
          }));
          console.log('[FamilyCompatibility] Formatted yogas:', formattedYogas.length, formattedYogas);

          setFamilyYogas(formattedYogas);
        } else {
          setFamilyYogas([]);
        }

      } catch (err: any) {
        console.error('[useFamilyCompatibilityData] Error:', err);
        console.error('[useFamilyCompatibilityData] Error message:', err?.message);
        console.error('[useFamilyCompatibilityData] Error stack:', err?.stack);
        setError(err.message || 'Failed to load family compatibility data');
        setMembers([]);
        setFamilyYogas([]);
      } finally {
        setLoading(false);
      }
    }, [userId, selectedProfileId]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    loadData();
  }, [userId, selectedProfileId, loadData]);

  // Function to generate yogas for family members who don't have them
  const generateMissingYogas = useCallback(async () => {
    if (!userId || members.length === 0) {
      console.log('[generateMissingYogas] Skipping - userId:', userId, 'members:', members.length);
      return;
    }

    console.log('[generateMissingYogas] Starting generation for', members.length, 'members');
    setGeneratingYogas(true);
    setYogaGenerationProgress({ current: 0, total: members.length, currentMember: '' });

    try {
      const profileIds = members.map(m => m.id);
      console.log('[generateMissingYogas] Profile IDs:', profileIds);
      
      const result = await batchGenerateFamilyYogas(
        userId,
        profileIds,
        (current, total, profileName) => {
          console.log(`[generateMissingYogas] Progress: ${current}/${total} - ${profileName}`);
          setYogaGenerationProgress({ current, total, currentMember: profileName });
        }
      );

      console.log('[useFamilyCompatibilityData] Batch yoga generation result:', result);

      // Log detailed errors for each profile
      if (!result.success) {
        console.error('[useFamilyCompatibilityData] Generation failed. Details:');
        result.results.forEach((r, idx) => {
          console.log(`  [${idx + 1}] ${r.profileName}:`, {
            stored: r.stored,
            skipped: r.skipped,
            errors: r.errors
          });
          // Log each error message explicitly
          if (r.errors && r.errors.length > 0) {
            r.errors.forEach((err, errIdx) => {
              console.error(`    Error ${errIdx + 1}: ${err}`);
            });
          }
        });
      }

      // Reload data to fetch newly generated yogas
      await loadData();

      setYogaGenerationProgress(null);
    } catch (err: any) {
      console.error('[useFamilyCompatibilityData] Yoga generation error:', err);
      setError(err.message || 'Failed to generate yogas');
      setYogaGenerationProgress(null);
    } finally {
      setGeneratingYogas(false);
    }
  }, [userId, members, loadData]);

  return { 
    members, 
    familyYogas, 
    loading, 
    error,
    generateMissingYogas,
    generatingYogas,
    yogaGenerationProgress
  };
}
